import { useForceSaveHandle } from "@components/Article/useForceSaveHandle";
import ArticleWithPreviewArticle from "@components/ArticlePage/ArticleWithPreviewArticle";
import { useRouter } from "@core/Api/useRouter";
import Path from "@core/FileProvider/Path/Path";
import type { ClientArticleProps } from "@core/SitePresenter/SitePresenter";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ArticlePropsService from "@core-ui/ContextServices/ArticleProps";
import ButtonStateService from "@core-ui/ContextServices/ButtonStateService/ButtonStateService";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import ResourceService from "@core-ui/ContextServices/ResourceService/ResourceService";
import Workspace from "@core-ui/ContextServices/Workspace";
import { useDebounce } from "@core-ui/hooks/useDebounce";
import ArticleContextWrapper from "@core-ui/ScopedContextWrapper/ArticleContextWrapper";
import useGetArticleContextData from "@core-ui/ScopedContextWrapper/useGetArticleContextData";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import { bindEditor } from "@core-ui/stores/EditorStore";
import { timing } from "@core-ui/timings";
import {
	createHandlePasteCallback,
	createOnUpdateCallback,
	createUpdateTitleFunction,
} from "@core-ui/utils/EditorCallbacks";
import BranchUpdaterService, {
	type OnBranchUpdateListener,
} from "@ext/git/actions/Branch/BranchUpdaterService/logic/BranchUpdaterService";
import OnBranchUpdateCaller from "@ext/git/actions/Branch/BranchUpdaterService/model/OnBranchUpdateCaller";
import { PublishEmitter } from "@ext/git/actions/Publish/logic/PublishEmitter";
import LoadingWithDiffBottomBar from "@ext/git/core/Diff/components/LoadingWithDiffBottomBar";
import { useEditorExtensions } from "@ext/git/core/Diff/components/store/EditorExtensionsStore";
import useDiff from "@ext/git/core/Diff/logic/hooks/useDiff";
import { useDiffExtensions } from "@ext/git/core/Diff/logic/hooks/useDiffExtensions";
import { useDiffStyles } from "@ext/git/core/Diff/logic/hooks/useDiffStyles";
import matchesDiffArticle from "@ext/git/core/Diff/logic/matchesDiffArticle";
import resetDiffBaseline from "@ext/git/core/Diff/logic/resetDiffBaseline";
import withoutInheritedTitleHandler from "@ext/git/core/Diff/logic/withoutInheritedTitleHandler";
import type { TreeReadScope } from "@ext/git/core/GitCommands/model/GitCommandsModel";
import { addEvent, Level, traced } from "@ext/loggers/opentelemetry";
import ArticleMat from "@ext/markdown/core/edit/components/ArticleMat";
import useContentEditorHooks from "@ext/markdown/core/edit/components/UseContentEditorHooks";
import getExtensions, { getTemplateExtensions } from "@ext/markdown/core/edit/logic/getExtensions";
import { useShouldShowInlineToolbar } from "@ext/markdown/core/edit/logic/hooks/useShouldShowInlineToolbar";
import ElementGroups from "@ext/markdown/core/element/ElementGroups";
import ArticleTitleHelpers from "@ext/markdown/elements/article/edit/ArticleTitleHelpers";
import { InlineToolbar } from "@ext/markdown/elements/article/edit/helpers/InlineToolbar";
import { editName as BLOCK_FIELD } from "@ext/markdown/elements/blockContentField/consts";
import { editName as BLOCK_PROPERTY } from "@ext/markdown/elements/blockProperty/consts";
import CommentEditorProvider from "@ext/markdown/elements/comment/edit/logic/CommentEditorProvider";
import useCommentCallbacks from "@ext/markdown/elements/comment/edit/logic/hooks/useCommentCallbacks";
import Comment from "@ext/markdown/elements/comment/edit/model/comment";
import Controllers from "@ext/markdown/elements/controllers/controllers";
import CopyArticles from "@ext/markdown/elements/copyArticles/copyArticles";
import { InlineLinkMenu } from "@ext/markdown/elements/link/edit/components/LinkMenu/InlineLinkMenu";
import OnAddMark from "@ext/markdown/elements/onAdd/OnAddMark";
import OnDeleteMark from "@ext/markdown/elements/onDocChange/OnDeleteMark";
import OnDeleteNode from "@ext/markdown/elements/onDocChange/OnDeleteNode";
import Placeholder from "@ext/markdown/elements/placeholder/placeholder";
import useEditorContext from "@ext/markdown/elementsUtils/editorContext/useEditorContext";
import PropertyService from "@ext/properties/components/PropertyService";
import { FileStatus } from "@ext/Watchers/model/FileStatus";
import type { DiffOptions } from "@gaurussel/tiptap-diff-utility";
import type { Editor, Extensions } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import { EditorContent, EditorContext, type JSONContent, useEditor } from "@tiptap/react";
import { useCallback, useEffect, useMemo, useRef } from "react";

const getReadOnlyExtensions = (exts: Extensions) => {
	const excludeExtensions = ["selectionMenu", "ArticleTitleHelpers"];
	return exts.filter((e) => !excludeExtensions.includes(e.name));
};

// block-field/block-property keep their content in the schema only when editable, and the diff
// has to render it — so they are dropped here and re-added by getTemplateExtensions(false)
const FILTER_MAIN_EXTENSIONS = [
	"onDeleteNode",
	"OnDeleteMark",
	"OnAddMark",
	"comment",
	"diff",
	"diffLines",
	BLOCK_FIELD,
	BLOCK_PROPERTY,
];

interface DiffModeViewProps {
	oldContent: JSONContent;
	newContent: JSONContent;
	changeType: FileStatus;
	articlePath: string;
	oldArticlePath?: string;
	oldScope?: TreeReadScope;
	newScope?: TreeReadScope;
	readOnly?: boolean;
	onUpdate?: (props: { editor: Editor; apiUrlCreator: ApiUrlCreator; articleProps: ClientArticleProps }) => void;
}

const DiffModeViewInternal = (props: DiffModeViewProps) => {
	const {
		oldContent,
		newContent,
		changeType,
		articlePath,
		oldArticlePath,
		oldScope,
		newScope,
		readOnly = false,
		onUpdate: currentOnUpdate,
	} = props;

	useDiffStyles();

	const extensions = useEditorExtensions();
	const resourceService = ResourceService.value;
	const router = useRouter();

	const handlePaste = createHandlePasteCallback(resourceService, PageDataContextService.value);
	const editorOnUpdate = createOnUpdateCallback();
	const editorTitleOnUpdate = createUpdateTitleFunction();
	const editorTitleOnUpdateRef = useRef(editorTitleOnUpdate);
	editorTitleOnUpdateRef.current = editorTitleOnUpdate;

	const propertyService = PropertyService.value;
	const propertyServiceRef = useRef(propertyService);
	propertyServiceRef.current = propertyService;
	const workspace = Workspace.current();
	const isGES = !!workspace?.enterprise?.gesUrl;
	const includeQuestions = isGES && !!workspace?.enterprise?.modules?.quiz;

	const articleProps = ArticlePropsService.value;
	const articlePropsRef = useRef(articleProps);
	articlePropsRef.current = articleProps;

	const catalogProps = useCatalogPropsStore((state) => state?.data);
	const oldContextArticlePath = Path.join(catalogProps?.basePath, oldArticlePath ?? articlePath);

	const isDelete = changeType === FileStatus.delete;
	const isAdded = changeType === FileStatus.new;

	const isTemplateInstance = articleProps.template?.length > 0;
	const contextArticlePath = Path.join(catalogProps?.basePath, articlePath);
	const apiUrlCreator = ApiUrlCreatorService.value;
	const apiUrlCreatorRef = useRef(apiUrlCreator);
	apiUrlCreatorRef.current = apiUrlCreator;

	// Read at call time: the handler lives in the editor's extensions, which outlive a render. Another
	// article's title must not be sent to the article the diff was opened for.
	const titleLoseFocusRef = useRef<
		(args: { newTitle: string; articleProps: ClientArticleProps; apiUrlCreator: ApiUrlCreator }) => unknown
	>(() => undefined);
	titleLoseFocusRef.current = ({
		newTitle,
		articleProps: editorArticleProps,
		apiUrlCreator: editorApiUrlCreator,
	}) => {
		if (!matchesDiffArticle(catalogProps?.basePath, editorArticleProps?.ref?.path, contextArticlePath)) return;
		return editorTitleOnUpdateRef.current(
			{
				apiUrlCreator: editorApiUrlCreator,
				articleProps: editorArticleProps,
				propertyService: propertyServiceRef.current,
			},
			router,
			newTitle,
		);
	};

	const onUpdateFlush = (editor: Editor) =>
		traced("diff-editor-save", { level: Level.Commands, omitArgs: true, omitResult: true }, async () => {
			const currentArticleProps = articlePropsRef.current;
			// The document in the editor belongs to `articlePath`; every write URL is built from
			// `currentArticleProps.ref.path`. If they drifted apart, drop the save instead of
			// writing this article's body into another article's file.
			if (!matchesDiffArticle(catalogProps?.basePath, currentArticleProps?.ref?.path, contextArticlePath)) {
				addEvent("article-mismatch", Level.Commands, {
					loaded: currentArticleProps?.ref?.path ?? "",
					target: contextArticlePath,
				});
				return;
			}

			await editorOnUpdate({
				editor,
				apiUrlCreator: apiUrlCreatorRef.current,
				articleProps: currentArticleProps,
			});
			// Dirty-check, not identity: this editor and articlePropsRef always describe the same,
			// currently open article (a switch remounts both). Differing title = the user edited
			// the H1 and it needs saving; `undefined` = empty doc, nothing to compare.
			const title = editor.state.doc.firstChild?.textContent;
			if (title !== undefined && currentArticleProps.title !== title) {
				await editorTitleOnUpdate(
					{
						apiUrlCreator: apiUrlCreatorRef.current,
						articleProps: currentArticleProps,
						propertyService: propertyServiceRef.current,
					},
					router,
					title,
				);
			}
		});

	const { start: onUpdateDebounce, cancel: cancelOnUpdateDebounce } = useDebounce(
		onUpdateFlush,
		timing("saveDebounceMs"),
	);

	const {
		apiUrlCreator: oldDiffArticleApiUrlCreator,
		catalogProps: oldDiffArticleCatalogProps,
		isLoading: isOldArticleContextDataLoading,
	} = useGetArticleContextData({
		articlePath: isAdded || isDelete ? null : oldContextArticlePath,
		catalogName: isAdded || isDelete ? null : catalogProps.name,
		scope: oldScope,
	});

	const hasChanges =
		changeType !== FileStatus.new && changeType !== FileStatus.delete && changeType !== FileStatus.current;

	const {
		extensions: newDiffExtensions,
		onEditorCreate: onNewEditorCreate,
		updateDiffOptions,
	} = useDiffExtensions({
		oldContent,
		isLeftPinned: true,
		oldScope,
		newScope,
		articlePath: oldContextArticlePath,
		oldApiUrlCreator: oldDiffArticleApiUrlCreator,
		oldCatalogProps: oldDiffArticleCatalogProps,
		isOldContextLoading: isOldArticleContextDataLoading,
	});

	const { onDeleteNodes, onDeleteMarks, onAddMarks } = useContentEditorHooks();

	const {
		onMarkAdded: onMarkAddedComment,
		onMarkDeleted: onMarkDeletedComment,
		onCommentSaved,
	} = useCommentCallbacks(articlePropsRef);

	const newEditorExtensionsBase = useMemo(
		() =>
			extensions
				? [
						...withoutInheritedTitleHandler(extensions).filter(
							(e) => !FILTER_MAIN_EXTENSIONS.includes(e.name),
						),
						...newDiffExtensions,
						ArticleTitleHelpers.configure({ onTitleLoseFocus: (args) => titleLoseFocusRef.current(args) }),
						OnDeleteNode.configure({ onDeleteNodes }),
						OnAddMark.configure({ onAddMarks }),
						OnDeleteMark.configure({ onDeleteMarks }),
						Controllers.configure({ editable: isTemplateInstance }),
						Comment.configure({
							onMarkAdded: onMarkAddedComment,
							onMarkDeleted: onMarkDeletedComment,
						}),
						...getTemplateExtensions(false),
					]
				: [
						...getExtensions({ isTemplateInstance, includeResources: true, includeQuestions }),
						Placeholder,
						Document.extend({ content: `paragraph ${ElementGroups.block}+` }),
						...newDiffExtensions,
						Controllers.configure({ editable: isTemplateInstance }),
						OnDeleteNode.configure({ onDeleteNodes }),
						OnAddMark.configure({ onAddMarks }),
						Comment.configure({
							onMarkAdded: onMarkAddedComment,
							onMarkDeleted: onMarkDeletedComment,
						}),
						CopyArticles.configure({ resourceService }),
						OnDeleteMark.configure({ onDeleteMarks }),
						ArticleTitleHelpers.configure({ onTitleLoseFocus: (args) => titleLoseFocusRef.current(args) }),
						...getTemplateExtensions(false),
					],
		[
			extensions,
			newDiffExtensions,
			onDeleteNodes,
			onAddMarks,
			onDeleteMarks,
			isTemplateInstance,
			onMarkAddedComment,
			onMarkDeletedComment,
			includeQuestions,
		],
	);

	const newEditorExtensions = useMemo(
		() => (readOnly ? getReadOnlyExtensions(newEditorExtensionsBase) : newEditorExtensionsBase),
		[readOnly, newEditorExtensionsBase],
	);

	const newEditorExtensionsWithContext = newEditorExtensions;

	const oldEditorExtensionsBase = useMemo(
		() =>
			extensions
				? [
						...getReadOnlyExtensions(extensions).filter((e) => e.name !== "comment"),
						Comment.configure({ appendCommentToBody: true }),
						...getTemplateExtensions(false),
					]
				: [
						...getExtensions({ isTemplateInstance, includeResources: true, includeQuestions }),
						Placeholder,
						Document.extend({ content: `paragraph ${ElementGroups.block}+` }),
						Comment.configure({ appendCommentToBody: true }),
						...getTemplateExtensions(false),
					],
		[extensions, isTemplateInstance, includeQuestions],
	);

	const oldEditorExtensionsWithContext = oldEditorExtensionsBase;

	const newEditor = useEditor(
		{
			extensions: newEditorExtensionsWithContext,
			content: newContent,
			editorProps: {
				handlePaste: (view, event, slice) =>
					handlePaste(view, event, slice, apiUrlCreatorRef.current, articlePropsRef.current, catalogProps),
			},
			editable: !readOnly,
			onCreate(props) {
				onNewEditorCreate(props.editor);
			},
			onUpdate: ({ editor }) => {
				if (!editor.isEditable) return;
				currentOnUpdate?.({
					editor,
					apiUrlCreator: apiUrlCreatorRef.current,
					articleProps: articlePropsRef.current,
				});
				onUpdateDebounce(editor);
			},
		},
		[newContent],
	);

	useEditorContext(newEditor);

	const oldContentEditor = useEditor(
		{
			extensions: oldEditorExtensionsWithContext,
			editable: false,
			content: oldContent,
		},
		[oldContent],
	);

	useEditorContext(oldContentEditor);

	// The only `EditorStore` registration in diff mode (`ContentEditor` is unmounted);
	// `getEditorStore().editor` readers depend on it.
	useEffect(() => {
		if (!newEditor) return;
		bindEditor(newEditor);
	}, [newEditor]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: onUpdateFlush reads current values via refs
	const forceSave = useCallback(async () => {
		if (!newEditor) return;
		cancelOnUpdateDebounce();
		await onUpdateFlush(newEditor);
	}, [newEditor, cancelOnUpdateDebounce]);

	useForceSaveHandle(forceSave);

	const shouldShow = useShouldShowInlineToolbar();

	if (!newEditor || !oldContentEditor) return null;

	const mainArticleWrapper = isDelete ? (
		<ArticleContextWrapper articlePath={oldContextArticlePath} scope={oldScope}>
			<ButtonStateService.Provider editor={oldContentEditor}>
				<EditorContext.Provider value={{ editor: oldContentEditor }}>
					<CommentEditorProvider editor={oldContentEditor} onCommentSaved={onCommentSaved}>
						<div>
							<InlineToolbar editor={oldContentEditor} shouldShow={shouldShow} />
							<InlineLinkMenu editor={oldContentEditor} />
							<EditorContent data-iseditable={false} data-qa="article-editor" editor={oldContentEditor} />
						</div>
					</CommentEditorProvider>
				</EditorContext.Provider>
			</ButtonStateService.Provider>
		</ArticleContextWrapper>
	) : (
		<CommentEditorProvider editor={newEditor} onCommentSaved={onCommentSaved}>
			<EditorContext.Provider value={{ editor: newEditor }}>
				<NewEditorWithDiff
					isUseDiff={hasChanges && !isOldArticleContextDataLoading}
					newApiUrlCreator={apiUrlCreator}
					newEditor={newEditor}
					oldApiUrlCreator={oldDiffArticleApiUrlCreator}
					oldEditor={oldContentEditor}
					readOnly={readOnly}
					updateDiffOptions={updateDiffOptions}
				/>
			</EditorContext.Provider>
		</CommentEditorProvider>
	);

	return (
		<>
			<ArticleWithPreviewArticle mainArticle={mainArticleWrapper} />
			<ArticleMat editor={newEditor} />
		</>
	);
};

interface NewEditorWithDiffProps {
	newEditor: Editor;
	oldEditor: Editor;
	readOnly: boolean;
	isUseDiff: boolean;
	newApiUrlCreator: ApiUrlCreator;
	oldApiUrlCreator: ApiUrlCreator;
	updateDiffOptions: (patch: Partial<DiffOptions>) => void;
}

const NewEditorWithDiff = (props: NewEditorWithDiffProps) => {
	const { newEditor, oldEditor, readOnly, isUseDiff, newApiUrlCreator, oldApiUrlCreator, updateDiffOptions } = props;

	const baseline = useCallback(() => (oldEditor && !oldEditor.isDestroyed ? oldEditor.state.doc : null), [oldEditor]);

	useDiff(
		isUseDiff
			? { newEditor, baseline, newApiUrlCreator, oldApiUrlCreator }
			: { newEditor: null, baseline: null, newApiUrlCreator: null, oldApiUrlCreator: null },
	);

	useEffect(() => {
		if (isUseDiff || !newEditor || newEditor.isDestroyed) return;
		newEditor.commands.updateDiffLinesModel([]);
	}, [isUseDiff, newEditor]);

	useEffect(() => {
		// No reset: the current state became the baseline, see `resetDiffBaseline`.
		const resetBaseline = () =>
			resetDiffBaseline(newEditor, oldEditor, (baseline) => updateDiffOptions({ baseline }));

		const onBranchUpdate: OnBranchUpdateListener = (_, caller) => {
			if (caller !== OnBranchUpdateCaller.DiscardNoReset) return;
			resetBaseline();
		};

		const publishToken = PublishEmitter.events.on("finish", resetBaseline);
		BranchUpdaterService.addListener(onBranchUpdate);
		return () => {
			PublishEmitter.events.off(publishToken);
			BranchUpdaterService.removeListener(onBranchUpdate);
		};
	}, [newEditor, oldEditor, updateDiffOptions]);

	const shouldShow = useShouldShowInlineToolbar();

	return (
		<ButtonStateService.Provider editor={newEditor}>
			<div>
				<InlineToolbar editor={newEditor} shouldShow={shouldShow} />
				<InlineLinkMenu editor={newEditor} />
				<EditorContent data-iseditable={!readOnly} data-qa="article-editor" editor={newEditor} />
			</div>
		</ButtonStateService.Provider>
	);
};

export const DiffModeView = (props: DiffModeViewProps) => {
	const { articlePath, newScope, oldScope, changeType } = props;
	const scope = changeType === FileStatus.delete ? oldScope : newScope;
	const catalogPath = useCatalogPropsStore((state) => state.data.basePath);

	return (
		<ArticleContextWrapper
			articlePath={Path.join(catalogPath, articlePath)}
			loader={<LoadingWithDiffBottomBar />}
			scope={scope}
		>
			<DiffModeViewInternal {...props} />
		</ArticleContextWrapper>
	);
};

export default DiffModeView;
